<template>
	<div class="test-wrapper">
		<!-- test entry page -->
		<section v-if="!testPreview">
			{{ $t('MAIN_CONTENT.testTaker.loading') }}
		</section>

		<section v-if="testPreview && !reviewStarted">
			<div class="pre-test">
				<div class="display-table">
					<div class="header text-center">
						<h2>{{ testPreview.TEST.TEST_TITLE }}</h2>
					</div>
					<div class="content">
						<p v-if="testPreview.TEST.TEST_DESCRIPTION" v-html="testPreview.TEST.TEST_DESCRIPTION"></p>
						<div class="test-meta">
							<div>
								<label>
									{{ $t('MAIN_CONTENT.testReviewer.started') }}
								</label>
								<div class="pill pill-gray-light">
									{{ testPreview.STUDENT_ATTEMPT.TEST_BEG_DATE }}
								</div>
							</div>
							<div>
								<label>
									{{ $t('MAIN_CONTENT.testReviewer.submitted') }}
								</label>
								<div class="pill pill-gray-light">
									{{ testPreview.STUDENT_ATTEMPT.TEST_END_DATE }}
								</div>
							</div>
							<div>
								<label>
									{{ $t('MAIN_CONTENT.commonContent.score') }}
								</label>
								<div class="pill pill-gray-light">
									{{ $t('MAIN_CONTENT.testReviewer.scoreOfTotalScore', { score: testPreview.STUDENT_ATTEMPT.TOTAL_SCORE, total: testPreview.TEST.TOTAL_SCORE } ) }}
								</div>
							</div>
						</div>
						<div class="foo text-center">
							<button class="btn btn-primary" @click="initReview()">
								{{ $t('MAIN_CONTENT.testReviewer.reviewMyTest') }} <i class="fas fa-chevron-right" />
							</button>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- test started -->
		<section v-if="testPreview && reviewStarted">
			<div class="test-header">
				<h1>{{ testPreview.TEST.TEST_TITLE }}</h1>
			</div> 
			<div class="row">
				<div class="col-md-9">
					<div id="testZone" class="testing-zone" tabindex="-1">
						<div class="total-score-badge-bar">
							{{ $t('MAIN_CONTENT.testReviewer.scoreOfTotalScorePossible', { score: testPreview.STUDENT_ATTEMPT.TOTAL_SCORE, total: testPreview.TEST.TOTAL_SCORE } ) }}
						</div>
						<question-group v-if="activeQuestion && activeQuestion.QUESTION_TYPE" :group="activeQuestionGroup" />

						<div v-if="activeQuestion && activeQuestion.QUESTION_TYPE" :aria-live="activeQuestion.SEQUENCE === 1 ? 'off' : 'assertive' ">
							<div class="extra-credit">
								<span v-if="activeQuestion.EXTRA_CREDIT_FLG" class="pill pill-success-lighten">
									<font-awesome-icon v-if="activeQuestion.EXTRA_CREDIT_FLG" 
																			icon="certificate" 
																			size="2x"
																			title="Extra Credit Question" /> 
									<span class="hidden-xs">
										{{ $t('MAIN_CONTENT.testReviewer.extraCredit') }}
									</span>
								</span>
							</div>

							<multiple-choice-question
									v-if="activeQuestion.QUESTION_TYPE === 'C'"
									:testPreview="testPreview"
									:activeQuestion="activeQuestion"
									:studentAnswer="studentAnswer"
									:studentAnswers="studentAnswers"
									:testMode="testModeActual" />

							<true-false-question
									v-if="activeQuestion.QUESTION_TYPE === 'T'" 
									:testPreview="testPreview"
									:activeQuestion="activeQuestion"
									:studentAnswer="studentAnswer"
									:studentAnswers="studentAnswers"
									:testMode="testModeActual"/>

							<short-fill-question
									v-if="activeQuestion.QUESTION_TYPE === 'S'"
									:testPreview="testPreview"
									:activeQuestion="activeQuestion"
									:studentAnswer="studentAnswer"
									:studentAnswers="studentAnswers"
									:blankAnswer="blankAnswer"
									:testMode="testModeActual" />

							<matching-question
									v-if="activeQuestion.QUESTION_TYPE === 'M'" 
									:testPreview="testPreview"
									:activeQuestion="activeQuestion"
									:studentAnswer="studentAnswer"
									:studentAnswers="studentAnswers"
									:questions="questions"
									:answers="answers"
									:test="test"
									:testMode="testModeActual"/>

							<numeric-question
									v-if="activeQuestion.QUESTION_TYPE === 'N'" 
									:testPreview="testPreview"
									:activeQuestion="activeQuestion"
									:studentAnswer="studentAnswer"
									:studentAnswers="studentAnswers"
									:blankAnswer="blankAnswer"
									:testMode="testModeActual"/>

							<essay-question
									v-if="activeQuestion.QUESTION_TYPE === 'E'" 
									:testPreview="testPreview"
									:activeQuestion="activeQuestion"
									:studentAnswer="studentAnswer"
									:studentAnswers="studentAnswers"
									:blankAnswer="blankAnswer"
									:testMode="testModeActual"/>
						</div>
						<!-- display the confirm panel -->
						<div v-else >
							<div class="panel panel-basic">
								<div class="panel-heading">
									<h2>{{ testPreview.TEST.TEST_TITLE }}</h2>
								</div>
								<div class="panel-body">
									<p>
										<b>
											{{ $t('MAIN_CONTENT.testReviewer.takeTester_finishedReview') }}
										</b>
									</p>
								</div>
								<div class="panel-footer">
									<button
											class="btn btn-primary"
											@click="closeWindow()">
										{{ $t('MAIN_CONTENT.testTaker.takeTester_closeWindow') }}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="col-md-3">
					<aside>
						<div class="question-nav">
							<question-selector />
						</div>
						<div class="score-wrap text-center">
							<span class="pill pill-dark">
								<strong>
									{{ $t('MAIN_CONTENT.testReviewer.scoreOutOfTotal', { score: testPreview.STUDENT_ATTEMPT.TOTAL_SCORE, total: testPreview.TEST.TOTAL_SCORE } ) }}
								</strong> {{ $t('MAIN_CONTENT.testReviewer.points') }} 
							</span>
						</div>
					</aside>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
	import { mapState,mapActions,mapMutations } from "vuex";
	import questionGroup from "./questionGroup.vue";
	import questionSelector from "./questionSelector.vue";
	import multipleChoiceQuestion from "./multipleChoiceQuestion.vue";
	import trueFalseQuestion from "./trueFalseQuestion.vue";
	import shortFillQuestion from "./shortFillQuestion.vue";
	import matchingQuestion from "./matchingQuestion.vue";
	import numericQuestion from "./numericQuestion.vue";
	import essayQuestion from "./essayQuestion.vue";
	import TestTakerResource from '../../api/TestTaker';

	export default {
			components: { 
					questionGroup, 
					questionSelector, 
					multipleChoiceQuestion, 
					trueFalseQuestion, 
					shortFillQuestion, 
					matchingQuestion,
					numericQuestion,
					essayQuestion
			},
			data (){
					return{
							reviewStarted: false
					};
			},
			computed: {
					...mapState( {
							testPreview: state =>{
									return state.testTaker.testPreview;
							},
							totalPointsEarned: state =>{
									return state.testTaker.totalPointsEarned;
							},
							activeQuestion: state =>{
									return state.testTaker.activeQuestion;
							},
							testStartTime: state =>{
									return state.testTaker.testAttemptStartTime;
							},
							testEndTime: state =>{
									return state.testTaker.testAttemptEndTime;
							},
							testMode: state =>{
									return state.testTaker.testMode;
							},
							studentAnswers: state =>{
									return state.testTaker.testPreview.STUDENT_ATTEMPT.ANSWERS;
							},
							blankAnswer:state =>{
									return state.testTaker.blankAnswerObj;
							},
							questions: state =>{
									return state.testTaker.testPreview.QUESTIONS;
							},
							answers: state =>{
									return state.testTaker.testPreview.ANSWERS;
							},
							test: state =>{
									return state.testTaker.testPreview.TEST;
							}
					} ),
					testModeActual(){
							if(this.testPreview.TEST.TEST_ENDED_FLG){
									return "grading";
							}

							return this.testMode;;
					},
					testEnded (){
							return this.testPreview.TEST.TEST_ENDED_FLG;
					},

					activeQuestionGroup (){
							return this.testPreview.QUESTION_GROUPS.filter( grp =>{
									return grp.GROUP_ID === this.activeQuestion.GROUP_ID; 
							} )[ 0 ];
					},
					blankAnswer() {
							return {
									"TEST_RID"                 : parseInt( this.testPreview.TEST.TEST_RID ),
									"TEST_QUESTION_RID"        : parseInt( this.activeQuestion.TEST_QUESTION_RID ),
									"TEST_QUESTION_PARENT_RID" : parseInt( this.activeQuestion.TEST_QUESTION_PARENT_RID),
									"TOTAL_POINTS"             : 0,
									"TEACHER_RID"              : "",
									"QUESTION_GRADED_STATUS"   : "",
									"QUESTION_TYPE"            : "",
									"ANSWER_FORMAT_TYPE"       : "",
									"SUB_QUESTIONS"            : {},
									"ACTUAL_ANSWER"            : "",
									"ANSWER_INFO"              : {
											"QUESTION_ANSWERED_BY"      : "",
											"QUESTION_GENERAL_FEEDBACK" : "",
											"ANSWER_SPECIFIC_FEEDBACK"  : "",
											"ANSWER_CORRECT_FLG"        : "",
											"POINTS_EARNED"             : 0,
											"QUESTION_GRADED_COMMENTS"  : "",
											"QUESTION_GRADED_BY"        : ""
									}
							}
					},
					studentAnswer(){
							return this.studentAnswers[ this.activeQuestion.TEST_QUESTION_RID ] ? this.studentAnswers[ this.activeQuestion.TEST_QUESTION_RID ] : [this.initBlankAnswer()];
					},
							

			},
			methods: {
					...mapActions( [ "load","submitTest" ] ),
					...mapMutations( [ "updateState","setAnswer" ] ),
					initBlankAnswer(){
						// TODO: const/let no vars
							var questionAnswer = JSON.parse( JSON.stringify( this.blankAnswer ) );
							questionAnswer.TEST_QUESTION_RID = this.activeQuestion.TEST_QUESTION_RID;
							return questionAnswer;
					},
					loadTest (){
							let queryParams = {
									student_rid: this.$route.query.student_rid,
									school_id: this.$route.query.school_id,
									course_id: this.$route.query.course_id,
									section_id: this.$route.query.section_id,
									teacher_rid: this.$route.query.teacher_rid
							};

							this.load( 
									{ 
											"key": "testPreview",
											"queryParams": queryParams, 
											"routeParams":{ 
													action: "review-test", 
													testRid: this.$route.params.testRid 
											} 
									}
							);
					},
					initReview (){
							// set a store prop so we know we're in review mode
							this.updateState( { key: "reviewMode", value: true } );
							// set the initial question ot be displayed and change the display
							this.updateState( { key: "activeQuestion", value: this.testPreview.QUESTIONS[ 0 ] } );
							this.reviewStarted = true;
					},
					closeWindow (){
							window.close();
					}
			},
			mounted (){
					this.loadTest();
			}
	};
</script>

<style scoped>
    .sticky-sideNav{
        max-width:35%;
    }

    .alert-bar{
        margin-top:-1px;
        margin-left:-1px;
    }
    h1, h2 {
        color: #0E76BD;
    }
</style>
